import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { 
  MultimodalResponse, 
  UploadedFile, 
  MultimodalSession,
  ResponseComparison
} from '../types/multimodal';
import { MultimodalService } from '../services/multimodalService';

interface MultimodalResultsProps {
  session: MultimodalSession | null;
  responses: MultimodalResponse[];
  isProcessing: boolean;
  onClose: () => void;
  onApplyResponse?: (response: MultimodalResponse) => void;
  onExportSession?: (session: MultimodalSession) => void;
}

export const MultimodalResults: React.FC<MultimodalResultsProps> = ({
  session,
  responses,
  isProcessing,
  onClose,
  onApplyResponse,
  onExportSession
}) => {
  const { t } = useTranslation();
  const { isPortuguese } = useLanguage();
  const [selectedTab, setSelectedTab] = useState<'responses' | 'comparison' | 'files' | 'stats'>('responses');
  const [comparison, setComparison] = useState<ResponseComparison | null>(null);
  const [expandedResponse, setExpandedResponse] = useState<string | null>(null);
  const multimodalService = new MultimodalService();

  // Comparar respostas quando mudarem
  useEffect(() => {
    if (responses.length > 1) {
      multimodalService.compareResponses(responses).then(setComparison);
    }
  }, [responses]);

  // Renderizar status de processamento
  const renderProcessingStatus = () => {
    if (!isProcessing && responses.length === 0) return null;

    const totalModels = session?.models.filter(m => m.enabled).length || 0;
    const completedResponses = responses.length;
    const processingProgress = totalModels > 0 ? (completedResponses / totalModels) * 100 : 0;

    return (
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium text-blue-800">
            {t('multimodal.processing', 'Processing with multiple models...')}
          </h3>
          <span className="text-sm text-blue-600">
            {completedResponses}/{totalModels}
          </span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${processingProgress}%` }}
          />
        </div>
        {isProcessing && (
          <p className="text-sm text-blue-600 mt-2">
            {t('multimodal.analyzing', 'AI models are analyzing your content...')}
          </p>
        )}
      </div>
    );
  };

  // Renderizar arquivo individual
  const renderFile = (file: UploadedFile, index: number) => {
    const getFileIcon = () => {
      switch (file.type) {
        case 'image': return '🖼️';
        case 'document': return '📄';
        case 'audio': return '🎵';
        case 'video': return '🎥';
        default: return '📁';
      }
    };

    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
      <div key={file.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
        <span className="text-2xl">{getFileIcon()}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">
            {file.name}
          </p>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span>{formatFileSize(file.size)}</span>
            {file.metadata?.width && file.metadata?.height && (
              <span>{file.metadata.width} × {file.metadata.height}px</span>
            )}
            {file.metadata?.duration && (
              <span>{Math.round(file.metadata.duration)}s</span>
            )}
            {file.metadata?.pages && (
              <span>{file.metadata.pages} páginas</span>
            )}
          </div>
        </div>
        {file.type === 'image' && file.url && (
          <img 
            src={file.url} 
            alt={file.name}
            className="w-12 h-12 object-cover rounded border"
          />
        )}
      </div>
    );
  };

  // Renderizar resposta individual
  const renderResponse = (response: MultimodalResponse, index: number) => {
    const isExpanded = expandedResponse === response.id;
    const isWinner = comparison?.winner === response.id;
    const hasError = !!response.error;

    return (
      <div 
        key={response.id} 
        className={`border-2 rounded-lg transition-all duration-200 ${
          isWinner ? 'border-green-300 bg-green-50' : 
          hasError ? 'border-red-300 bg-red-50' : 
          'border-gray-200 bg-white hover:border-purple-300'
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-medium ${
                isWinner ? 'bg-green-500' : hasError ? 'bg-red-500' : 'bg-purple-500'
              }`}>
                {isWinner ? '👑' : hasError ? '❌' : index + 1}
              </div>
              <div>
                <h3 className="font-medium text-gray-800">{response.modelName}</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>{response.tokens.total} {t('multimodal.tokens', 'tokens')}</span>
                  <span>{response.processingTime}ms</span>
                  <span>{response.timestamp.toLocaleTimeString()}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isWinner && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                  {t('multimodal.bestResponse', 'Best Response')}
                </span>
              )}
              {onApplyResponse && !hasError && (
                <button
                  onClick={() => onApplyResponse(response)}
                  className="px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-colors"
                >
                  {t('multimodal.apply', 'Apply')}
                </button>
              )}
              <button
                onClick={() => setExpandedResponse(isExpanded ? null : response.id)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg 
                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`transition-all duration-200 ${isExpanded ? 'max-h-none' : 'max-h-32 overflow-hidden'}`}>
          <div className="p-4">
            {hasError ? (
              <div className="text-red-600">
                <p className="font-medium">{t('multimodal.error', 'Error')}</p>
                <p className="text-sm">{response.error}</p>
              </div>
            ) : (
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                  {response.content}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Truncated indicator */}
        {!isExpanded && !hasError && response.content.length > 200 && (
          <div className="px-4 pb-2">
            <button
              onClick={() => setExpandedResponse(response.id)}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              {t('multimodal.readMore', 'Read more...')}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Renderizar comparação
  const renderComparison = () => {
    if (!comparison || responses.length <= 1) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">
            {t('multimodal.noComparison', 'Comparison will be available when multiple responses are received.')}
          </p>
        </div>
      );
    }

    const validResponses = responses.filter(r => !r.error);

    return (
      <div className="space-y-6">
        {/* Metrics Overview */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600">
              {Math.round(comparison.metrics.quality * 100)}%
            </div>
            <div className="text-sm text-blue-800">
              {t('multimodal.quality', 'Average Quality')}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">
              {Math.round(comparison.metrics.relevance * 100)}%
            </div>
            <div className="text-sm text-green-800">
              {t('multimodal.relevance', 'Average Relevance')}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round(comparison.metrics.completeness * 100)}%
            </div>
            <div className="text-sm text-purple-800">
              {t('multimodal.completeness', 'Average Completeness')}
            </div>
          </div>
        </div>

        {/* Response Rankings */}
        <div>
          <h3 className="font-medium text-gray-800 mb-4">
            {t('multimodal.responseRanking', 'Response Ranking')}
          </h3>
          <div className="space-y-3">
            {validResponses
              .sort((a, b) => b.content.length - a.content.length) // Simple ranking by length
              .map((response, index) => (
                <div key={response.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                    index === 0 ? 'bg-gold' : index === 1 ? 'bg-gray-400' : 'bg-gray-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{response.modelName}</div>
                    <div className="text-sm text-gray-500">
                      {response.content.length} {t('multimodal.chars', 'chars')} • {response.tokens.total} {t('multimodal.tokens', 'tokens')}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400">
                    {response.processingTime}ms
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar estatísticas
  const renderStats = () => {
    if (!session) return null;

    const stats = multimodalService.getSessionStats(session);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600">{stats.totalResponses}</div>
            <div className="text-sm text-blue-800">{t('multimodal.totalResponses', 'Total Responses')}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{stats.successfulResponses}</div>
            <div className="text-sm text-green-800">{t('multimodal.successful', 'Successful')}</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-600">{stats.totalTokens}</div>
            <div className="text-sm text-purple-800">{t('multimodal.totalTokens', 'Total Tokens')}</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-orange-600">{stats.avgProcessingTime}ms</div>
            <div className="text-sm text-orange-800">{t('multimodal.avgTime', 'Avg Time')}</div>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-800 mb-3">
            {t('multimodal.sessionInfo', 'Session Information')}
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">{t('multimodal.files', 'Files')}:</span>
              <span className="ml-2 font-medium">{stats.files}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('multimodal.models', 'Active Models')}:</span>
              <span className="ml-2 font-medium">{stats.models}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('multimodal.created', 'Created')}:</span>
              <span className="ml-2 font-medium">{session.createdAt.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('multimodal.updated', 'Updated')}:</span>
              <span className="ml-2 font-medium">{session.updatedAt.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {onExportSession && (
          <div className="text-center">
            <button
              onClick={() => onExportSession(session)}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t('multimodal.exportSession', 'Export Session')}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!session && responses.length === 0 && !isProcessing) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                🎯 {t('multimodal.results', 'Multimodal Results')}
              </h2>
              <p className="text-purple-100 mt-1">
                {t('multimodal.analysisComplete', 'AI analysis completed')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Processing Status */}
        {isProcessing && (
          <div className="p-6">
            {renderProcessingStatus()}
          </div>
        )}

        {/* Tabs */}
        {!isProcessing && responses.length > 0 && (
          <div className="border-b border-gray-200">
            <nav className="flex px-6">
              {[
                { id: 'responses', label: t('multimodal.responses', 'Responses') },
                { id: 'comparison', label: t('multimodal.comparison', 'Comparison') },
                { id: 'files', label: t('multimodal.files', 'Files') },
                { id: 'stats', label: t('multimodal.statistics', 'Statistics') }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id as any)}
                  className={`px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                    selectedTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 pb-8">
          {selectedTab === 'responses' && (
            <div className="space-y-4 pb-6">
              {responses.map((response, index) => renderResponse(response, index))}
              {responses.length === 0 && !isProcessing && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {t('multimodal.noResponses', 'No responses available yet.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {selectedTab === 'comparison' && renderComparison()}

          {selectedTab === 'files' && (
            <div className="space-y-3 pb-6">
              {session?.files.map((file, index) => renderFile(file, index))}
              {(!session?.files || session.files.length === 0) && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {t('multimodal.noFiles', 'No files uploaded.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {selectedTab === 'stats' && renderStats()}
          </div>
        </div>
      </div>
    </div>
  );
};